import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setStoredToken } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=loading, false=anon, object=user
  const [loading, setLoading] = useState(true);

  // Post-kontekst: bruker poster enten som seg selv (null) eller som et par (couple_id).
  // Lagres i localStorage så valget overlever sidelast.
  const [postingAsCoupleId, setPostingAsCoupleIdState] = useState(() => {
    try { return localStorage.getItem("ne_post_as_couple_id") || null; } catch { return null; }
  });
  const [linkedCouple, setLinkedCouple] = useState(null); // {id, name, handle, ...} hvis user er knyttet til et par

  const setPostingAsCoupleId = useCallback((id) => {
    setPostingAsCoupleIdState(id);
    try {
      if (id) localStorage.setItem("ne_post_as_couple_id", id);
      else localStorage.removeItem("ne_post_as_couple_id");
    } catch { /* ignore */ }
  }, []);

  // Hent par-info når user lastes
  const loadLinkedCouple = useCallback(async (currentUser) => {
    if (!currentUser || currentUser === false) { setLinkedCouple(null); return; }
    const hasCouple = currentUser.role === "couple" || currentUser.linked_couple_id;
    if (!hasCouple) { setLinkedCouple(null); return; }
    try {
      const { data } = await api.get("/me/couple");
      if (data && data.status === "active") {
        setLinkedCouple(data);
        // Hvis brukeren er en couple-user, default til å poste SOM PARET ved første lasting.
        if (currentUser.role === "couple") {
          const stored = (() => { try { return localStorage.getItem("ne_post_as_couple_id"); } catch { return null; } })();
          if (!stored) setPostingAsCoupleId(data.id);
        }
      } else {
        setLinkedCouple(null);
        setPostingAsCoupleId(null);
      }
    } catch {
      setLinkedCouple(null);
    }
  }, [setPostingAsCoupleId]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadLinkedCouple(data);
    } catch {
      setUser(false);
      setLinkedCouple(null);
      setPostingAsCoupleId(null);
    } finally {
      setLoading(false);
    }
  }, [loadLinkedCouple, setPostingAsCoupleId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Marker body med guest-status + jobbsafe, slik at CSS kan blure eksplisitt innhold globalt
  useEffect(() => {
    const isGuest = user === false;
    document.body.dataset.guest = isGuest ? "true" : "false";
    const prefs = (user && typeof user === "object" && user.content_prefs) || {};
    document.body.dataset.jobbsafe = prefs.jobbsafe_blur ? "true" : "false";
    return () => {
      delete document.body.dataset.guest;
      delete document.body.dataset.jobbsafe;
    };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    // Hent UTM-attribusjon fra sessionStorage (satt ved første landing av usePageViewTracker)
    let utm = {};
    try {
      const stored = sessionStorage.getItem("ne_utm");
      if (stored) {
        const u = JSON.parse(stored);
        utm = {
          utm_source: u.utm_source || null,
          utm_campaign: u.utm_campaign || null,
          utm_medium: u.utm_medium || null,
          utm_content: u.utm_content || null,
        };
      }
    } catch { /* ignore */ }
    const { data } = await api.post("/auth/register", { ...payload, ...utm });
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const registerCouple = async (payload) => {
    const { data } = await api.post("/couples/register-dual", payload);
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setStoredToken(null);
    setUser(false);
    setLinkedCouple(null);
    setPostingAsCoupleId(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, registerCouple, logout, refresh,
      linkedCouple, postingAsCoupleId, setPostingAsCoupleId,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
